import React, { useEffect, useRef, useState } from "react";
import "./GenericChatbotContainer.css";
import iq_api_v2 from "../../../../services/iqApiV2";
import { useParams } from "react-router-dom";
import { useUser } from "../../../../context/UserContext";
import { useDispatch } from "react-redux";
import { fetchOpportunitiesRequest } from "../../../../store/features/actions/opportunities/opportunitesActions";
import IOSSpinner from "../../../HtmlElements/IOSSpinner";
import { useGroupTaskWebSocket } from "../../../../context/GroupTaskWebSocketContext";
import { WSS_URL } from "../../../../config/apiConfig";
import GeneratingLoader from "../uiElements/GeneratingLoader";

const FindOppsChatContainer = ({ onBack, type = "spot" }) => {
  const { clientName, accountName } = useParams();
  const { user } = useUser();
  const dispatch = useDispatch();
  const { registerTask } = useGroupTaskWebSocket();

  const [history, setHistory] = useState("");
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [isSpotFind, setIsSpotFind] = useState(true);

  const inputHistRef = useRef("");
  const oppRespRef = useRef([]);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);

  const getTime = () =>
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // -------- Small card to display each found opp with Save button ----------
  const PostFindAddOpportunityCard = ({ index, opp }) => {
    const [saved, setSaved] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const handleSaveOpp = async (opp) => {
      setIsSaving(true);

      const data = {
        client: clientName,
        account: accountName,
        opportunity_name: opp.opportunity_name || "",
        opportunity_description: opp.opportunity_description || "",
        user_id: user?.id,
        signal: opp?.signal || "",
        type,
        history: inputHistRef.current,
        mapped_products: JSON.stringify(opp.mapped_products || []),
        relevance: opp?.relevance || "",
        sub_signal: opp?.sub_signal ? JSON.stringify(opp?.sub_signal) : null,
        unmet_need: opp?.unmet_need ? JSON.stringify(opp?.unmet_need) : null,
        what_is_the_opportunity:opp?.what_is_the_opportunity ? JSON.stringify(opp?.what_is_the_opportunity) : null
      };

      pushMessage("SalesPlay", "", true); // Show loading

      try {
        const response = await iq_api_v2.post(
          "opportunities/add_opportunity",
          {},
          { params: data }
        );
        setSaved(true);
        setIsSaving(false);
        handlePostSuccess(response);
        registerTask("spot_opp_table", `${WSS_URL}/${response?.data?.task_id}`);
      } catch (err) {
        setSaved(false);
        setIsSaving(false);
        updateLastMessage(
          `${err?.response?.data?.detail || "Cannot Add Opportunity"}`
        );
        const response = {
          data: { next_prompt: "Failed To Add Opportunity. Please retry" },
        };
        handlePostSuccess(response);
        console.log("error", err);
      }
    };

    return (
      <div className="post-find-add-opp-container">
        <div className="post-find-add-opp-content fw-bold">
          {index + 1}. {opp?.opportunity_name}
        </div>

        <div className="post-find-add-opp-content">
          <div
            className={`post-find-add-opp-content ${
              expanded ? "expanded" : "collapsed"
            }`}
          >
            {expanded
              ? opp?.opportunity_description
              : (opp?.opportunity_description || "").slice(0, 100) +
                ((opp?.opportunity_description || "").length > 100 ? "..." : "")}
          </div>
          {opp?.opportunity_description?.length > 150 && (
            <span
              className="read-more-toggle"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "Read less" : "Read more"}
            </span>
          )}
        </div>

        <div className="post-find-add-opp-content">
          {isSaving ? (
            <IOSSpinner />
          ) : (
            <button
              className={`find-opp-catagory-container-btn${
                saved ? "-saved" : ""
              }`}
              disabled={isSaving || saved}
              onClick={() => handleSaveOpp(opp)}
            >
              {saved ? "Saved" : "Save Opportunity"}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ------------------ Chat helpers -------------------
  const pushMessage = (sender, message, isLoading = false) => {
    setChatMessages((prev) => [
      ...prev,
      { sender, message, isLoading, timestamp: !isLoading ? getTime() : undefined },
    ]);
  };

  const updateLastMessage = (message) => {
    setChatMessages((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        message,
        isLoading: false,
        timestamp: getTime(),
      };
      return updated;
    });
  };

  const handlePostSuccess = (response) => {
    const nextPrompt =
      response?.data?.next_prompt ||
      `Thank you, ${user?.firstname}. I'm creating documents for your Saved Opportunities. \n\n You will see them inside ${type} opportunities in the next 1-2 minutes.`;
    const historyTxt = response?.data?.task_id === 0 ? response?.data?.history : "";
    const shouldFetch = response?.data?.task_id === 1;

    setHistory(historyTxt);
    updateLastMessage(nextPrompt);

    if (shouldFetch) {
      dispatch(
        fetchOpportunitiesRequest({
          clientName,
          accountName,
          userId: user?.id,
          type,
        })
      );
    }
  };

  // ----------------- Main handler (FIND ONLY) -----------------
  const handleAPIFindOpps = async (input) => {
    if (!input?.trim()) return;

    let actualInput = input;
    const userName = user?.firstname || "User";

    // Repeat search using previous input with exclusions
    if (input === "find_more_opportunitites") {
      actualInput = inputHistRef.current;
      if (!actualInput) {
        pushMessage("SalesPlay", "❗ No previous search found to base this on.");
        return;
      }
      const exclusions =
        oppRespRef.current?.map((o) => o?.opportunity_name).join(", ") || "";
      pushMessage(
        "SalesPlay",
        <div
          style={{
            maxHeight: "5rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <GeneratingLoader defaultFontSize="1rem" />
        </div>
      );
      actualInput = `${actualInput} (excluding: ${exclusions})`;
    } else {
      inputHistRef.current = input;
      pushMessage(userName, input);
    }

    setMessage("");

    // FIND FLOW
    if (isSpotFind) {
      if (input !== "find_more_opportunitites") {
        pushMessage(
          "SalesPlay",
          <div style={{ maxHeight: "5rem" }}>
            <GeneratingLoader defaultFontSize="1rem" />
          </div>
        );
      }

      try {
        const params = {
          client: clientName,
          account: accountName,
          company_type: "public",
          user_id: user?.id,
          user_input: actualInput,
          type,
          history,
          opportunities_generation_count:3,
          gpt5:1
        };

        const response = await iq_api_v2.post(
          "/opportunities/find_opportunities",
          {},
          { params }
        );

        // remove loading bubble if present
        setChatMessages((prev) => {
          const updated = [...prev];
          if (updated.length > 0 && updated[updated.length - 1].isLoading) {
            updated.pop();
          }
          return updated;
        });

        const opportunities = response?.data?.opportunities || [];
        oppRespRef.current = opportunities;
        updateLastMessage("Below are the found opportunities.");

        if (Array.isArray(opportunities) && opportunities.length > 0) {
          opportunities.forEach((opp, index) => {
            pushMessage(
              "SalesPlay",
              <PostFindAddOpportunityCard
                index={index}
                opp={opp}
                key={`PostFindAddOpportunityCard${index}`}
              />
            );
          });
        } else {
          pushMessage("SalesPlay", "No opportunities found.");
        }

        // Follow-up options (Find more)
        pushMessage(
          "SalesPlay",
          <div>
            <span>Did not find the opportunity you are looking for?</span>
            <div className="find-opp-catagory-container">
              <button
                className="find-opp-catagory-container-btn"
                onClick={() => handleAPIFindOpps("find_more_opportunitites")}
              >
                Find more opportunities
              </button>
            </div>
          </div>
        );
      } catch (err) {
        updateLastMessage(
          ` ${err?.response?.data?.message || "No results found."}`
        );
      }
    }
  };

  // ----------------- UI render helpers -----------------
  const renderMessages = (messages) =>
    messages.map((msg, index) => {
      const isUser = msg.sender === user?.firstname;
      const prevSender = index > 0 ? messages[index - 1].sender : null;
      const showSender = msg.sender !== prevSender;
      return (
        <div
          key={index}
          className={`message-wrapper ${isUser ? "align-right" : "align-left"}`}
        >
          {showSender && <p className="sender-name">{msg.sender}</p>}
          <div
            className={`message-bubble-${isUser ? "User" : msg.sender} ${
              msg.isLoading ? "is-loading" : ""
            }`}
          >
            <div className="message-content">{msg.message}</div>
            {!msg.isLoading && msg.timestamp && (
              <div
                className={`message-timestamp-${isUser ? "User" : msg.sender}`}
              >
                {msg.timestamp}
              </div>
            )}
          </div>
        </div>
      );
    });

  // ----------------- Effects -----------------
  useEffect(() => {
    // Default to FIND flow only
    setIsSpotFind(true);
    setChatMessages([
      {
        sender: "SalesPlay",
        message: (
          <>
            <div>
              {`Hi ${user?.firstname}! 👋 Tell me in 1–2 lines what kinds of opportunities you want me to find.`}
            </div>
            <br />
            <div>
              For example:
              <br />
              <em>"I am interested in opportunities related to AI"</em>
            </div>
          </>
        ),
        timestamp: getTime(),
      },
    ]);
  }, [user?.firstname]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // ----------------- Render -----------------
  return (
    <div className="chatbox-container">
      <div className="chatbox-header" onClick={onBack}>
        <img className="back-btn-img" src="/img/back_top.png" alt="back" />
        <span className="back_chat_top">Back</span>
      </div>

      <div className="chatbox-messages-container" ref={containerRef}>
        {renderMessages(chatMessages)}
      </div>

      {isSpotFind && (
        <div className="chatbox-input-container">
          <textarea
            ref={textareaRef}
            className="chatbox-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAPIFindOpps(message.trim());
              }
            }}
            // placeholder="Describe your interest (e.g., AI in manufacturing, sustainability initiatives, etc.)"
          />
          <button
            className="send-button"
            onClick={() => handleAPIFindOpps(message)}
          >
            <img className="send-button-img" src="/img/arrow-up.png" alt="send" />
          </button>
        </div>
      )}
    </div>
  );
};

export default FindOppsChatContainer;
